import { QueryOrdering } from '@/api/generated/model';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { RowKind } from '@/domains/home/components/home-tabs/user-intelligence/_components';
import { tooltipMap } from '@/domains/home/components/home-tabs/user-intelligence/constants';
import { InteractionAggregates } from '@/models/interaction-aggregates/internal-models';
import { GroupByValue } from '@/types/groupBy';
import { calcPercentageDiff } from '@/utils/numbers.util';

export type BaseIntelligenceTableRow = {
  row_kind?: RowKind;
  n_users: number;
  title: string;
  title2?: string;
  tooltip?: string;
  n_interactions: number;
  createdAt?: Date;
  n_interactions_delta?: number | null;
  n_users_delta?: number | null;
  n_positive_feedback?: number;
  n_positive_feedback_prev: number | undefined;
  n_negative_feedback?: number;
  n_negative_feedback_prev: number | undefined;
  error_rate?: number;
  error_rate_prev?: number;
  error_rate_explicit?: number;
  error_rate_explicit_prev?: number;
  percentage_negative_feedback_explicit?: number;
  percentage_negative_feedback_explicit_prev?: number;
  conversation_percentage_negative_feedback_explicit?: number;
  conversation_percentage_negative_feedback_explicit_prev?: number;
  n_positive_explicit_feedback?: number;
  n_negative_explicit_feedback?: number;
  n_positive_explicit_feedback_prev?: number;
  n_negative_explicit_feedback_prev?: number;
  explicit_feedback_ratio?: number;
  explicit_feedback_ratio_prev?: number;
  n_paste_output?: number;
  n_paste_output_prev?: number;
  is_custom?: boolean;
  retention: number | undefined;
  retention_delta: number | undefined;
  session_length: number | undefined;
  session_length_prev: number | undefined;
  session_length_delta: number | null;
  session_time: number | undefined;
  session_time_prev: number | undefined;
  session_time_delta: number | null;
  n_sessions: number | undefined;
  n_sessions_prev: number | undefined;
  n_sessions_delta: number | null;
  sort_percentage: number | undefined;
  sentiment_score: number | undefined;
  color?: string;
  n_positive_messages: number | undefined;
  n_positive_messages_prev: number | undefined;
  n_negative_messages: number | undefined;
  n_negative_messages_prev: number | undefined;
  n_conversations_positive_explicit: number | undefined;
  n_conversations_positive_explicit_prev: number | undefined;
  n_conversations_negative_explicit: number | undefined;
  n_conversations_negative_explicit_prev: number | undefined;
  // should be only in IntelligenceTableRow
  start_session?: string;
  n_data_sources?: number;
  bounce_rate?: number;
  bounce_rate_prev?: number;
  cost: number;
  n_intents: number;
  n_user_actions: number;
  penetration: number | undefined;
  penetration_prev: number | undefined;
  penetration_delta: number | null;
  user_rating?: number;
  user_rating_prev?: number;
  description?: string;
  quote?: string;
  is_custom_aggregate_category?: boolean;
  roi_hours_saved?: number;
  roi_hours_saved_prev?: number;
  roi_hours_saved_per_user?: number;
  roi_hours_saved_per_user_prev?: number;
  roi_hours_saved_per_interaction?: number;
  roi_hours_saved_per_interaction_prev?: number;
  roi_successful_interactions?: number;
  roi_successful_interactions_prev?: number;
  roi_success_rate?: number;
  roi_success_rate_prev?: number;
  roi_value_saved?: number;
  roi_value_saved_prev?: number;
  roi_value_saved_per_interaction?: number;
  roi_value_saved_per_interaction_prev?: number;
  custom_category_created_at?: string;
  custom_variables?: Record<string, number | null>;
  latency_seconds?: number;
  latency_seconds_prev?: number;
  input_length?: number;
  input_length_prev?: number;
  output_length?: number;
  output_length_prev?: number;
  total_users?: number;
  input_tokens?: number;
  output_tokens?: number;
  aifi_score?: number;
  aifi_score_prev?: number;
};

export type IntelligenceTableRow = BaseIntelligenceTableRow;

export type IntelligenceDetailsTableRow = BaseIntelligenceTableRow & {
  intent_is_merged: boolean;
};

// =====================================================
// MAPPERS
// =====================================================

export function baseMapToIntelligenceRow(
  agg: InteractionAggregates,
  enableLLMIssuesV2 = false,
): BaseIntelligenceTableRow {
  return {
    n_interactions: agg.nInteractions ?? 0,
    n_users: agg.nUsers ?? 0,
    n_interactions_delta: calcPercentageDiff(agg.nInteractions, agg.nInteractionsPrev),
    n_users_delta: calcPercentageDiff(agg.nUsers, agg.nUsersPrev),
    title: agg.groupName,
    title2: agg.groupName2,
    createdAt: new Date(),
    n_negative_feedback: enableLLMIssuesV2
      ? (agg.nNegativeImplicitUserFeedbackV2 ?? 0)
      : (agg.nNegativeImplicitUserFeedback ?? 0),
    n_positive_feedback: (agg.nPositiveExplicitUserFeedback ?? 0) + (agg.nPositiveImplicitUserFeedback ?? 0),
    n_positive_explicit_feedback: enableLLMIssuesV2
      ? (agg.nPositiveExplicitUserFeedbackV2 ?? 0)
      : (agg.nPositiveExplicitUserFeedback ?? 0),
    n_negative_explicit_feedback: enableLLMIssuesV2
      ? (agg.nNegativeExplicitUserFeedbackV2 ?? 0)
      : (agg.nNegativeExplicitUserFeedback ?? 0),
    n_positive_explicit_feedback_prev: agg.nPositiveExplicitUserFeedbackPrev ?? 0,
    n_negative_explicit_feedback_prev: agg.nNegativeExplicitUserFeedbackPrev ?? 0,
    retention: agg.retention,
    retention_delta: calcPercentageDiff(agg.retention, agg.retentionPrev) ?? undefined,
    n_negative_feedback_prev:
      (agg.nNegativeExplicitUserFeedbackPrev ?? 0) + (agg.nNegativeImplicitUserFeedbackPrev ?? 0),
    n_positive_feedback_prev:
      (agg.nPositiveExplicitUserFeedbackPrev ?? 0) + (agg.nPositiveImplicitUserFeedbackPrev ?? 0),
    explicit_feedback_ratio: agg.explicitFeedbackRatio,
    explicit_feedback_ratio_prev: agg.explicitFeedbackRatioPrev,
    n_paste_output: agg.nPasteOutput,
    n_paste_output_prev: agg.nPasteOutputPrev,
    error_rate: agg.errorRate,
    error_rate_prev: agg.errorRatePrev,
    error_rate_explicit: agg.errorRateExplicit,
    error_rate_explicit_prev: agg.errorRateExplicitPrev,
    session_length: agg.sessionLength,
    session_length_prev: agg.sessionLengthPrev,
    session_length_delta: calcPercentageDiff(agg.sessionLength, agg.sessionLengthPrev),
    session_time: agg.sessionTime,
    session_time_prev: agg.sessionTimePrev,
    session_time_delta: calcPercentageDiff(agg.sessionTime, agg.sessionTimePrev),
    n_sessions: agg.nSessions,
    n_sessions_prev: agg.nSessionsPrev,
    n_sessions_delta: calcPercentageDiff(agg.nSessions, agg.nSessionsPrev),
    sentiment_score: agg.sentimentScore,
    n_positive_messages: agg.nPositiveMessages,
    n_negative_messages: agg.nNegativeMessages,
    n_positive_messages_prev: agg.nPositiveMessagesPrev,
    n_negative_messages_prev: agg.nNegativeMessagesPrev,
    sort_percentage: 0,
    bounce_rate: agg.bounceRate,
    bounce_rate_prev: agg.bounceRatePrev,
    n_conversations_positive_explicit: agg.nConversationsPositiveExplicit,
    n_conversations_positive_explicit_prev: agg.nConversationsPositiveExplicitPrev,
    n_conversations_negative_explicit: agg.nConversationsNegativeExplicit,
    n_conversations_negative_explicit_prev: agg.nConversationsNegativeExplicitPrev,
    cost: agg.cost ?? 0,
    n_intents: agg.nUserIntents ?? 0,
    n_user_actions: agg.nUserActions ?? 0,
    penetration: agg.penetration,
    penetration_prev: agg.penetrationPrev,
    penetration_delta: calcPercentageDiff(agg.penetration, agg.penetrationPrev),
    description: agg.description,
    quote: agg.quote,
    is_custom_aggregate_category: agg.isCustomAggregateCategory,
    roi_hours_saved: agg.roiHoursSaved,
    roi_hours_saved_prev: agg.roiHoursSavedPrev,
    roi_hours_saved_per_user: agg.roiHoursSavedPerUser,
    roi_hours_saved_per_user_prev: agg.roiHoursSavedPerUserPrev,
    roi_hours_saved_per_interaction: agg.roiHoursSavedPerInteraction,
    roi_hours_saved_per_interaction_prev: agg.roiHoursSavedPerInteractionPrev,
    roi_successful_interactions: agg.roiSuccessfulInteractions,
    roi_successful_interactions_prev: agg.roiSuccessfulInteractionsPrev,
    roi_success_rate: agg.roiSuccessRate,
    roi_success_rate_prev: agg.roiSuccessRatePrev,
    roi_value_saved: agg.roiValueSaved,
    roi_value_saved_prev: agg.roiValueSavedPrev,
    roi_value_saved_per_interaction: agg.roiValueSavedPerInteraction,
    roi_value_saved_per_interaction_prev: agg.roiValueSavedPerInteractionPrev,
    custom_category_created_at: agg.customCategoryCreatedAt,
    custom_variables: agg.customVariables ?? {},
    latency_seconds: agg.latencySeconds,
    latency_seconds_prev: agg.latencySecondsPrev,
    input_length: agg.inputLength,
    input_length_prev: agg.inputLengthPrev,
    output_length: agg.outputLength,
    output_length_prev: agg.outputLengthPrev,
    input_tokens: agg.inputTokens,

    output_tokens: agg.outputTokens,

    total_users: agg.totalUsers,
    aifi_score: agg.aifiScore,
    aifi_score_prev: agg.aifiScorePrev,
  };
}

export function mapToIntelligenceTableRow(
  agg: InteractionAggregates,
  deps: {
    sortBy: QueryOrdering;
    groupBy: GroupByValue | undefined;
    isLoadingAggregates: boolean;
    aggregateBy: AggregateBy;
    enableLLMIssuesV2?: boolean;
    isCustomAggregateCategory?: boolean;
  },
): IntelligenceTableRow {
  const { groupBy, enableLLMIssuesV2, isCustomAggregateCategory } = deps;

  return {
    ...baseMapToIntelligenceRow(agg, enableLLMIssuesV2),
    is_custom_aggregate_category: agg.isCustomAggregateCategory || isCustomAggregateCategory,
    is_custom: agg.isCustom,
    retention: agg.retention,
    sentiment_score: agg.sentimentScore,
    sort_percentage: agg.sortPercentage,
    start_session: agg.startSession,
    tooltip:
      groupBy === 'emotions'
        ? tooltipMap.emotions[agg.groupName]
        : groupBy === 'sentiment'
          ? tooltipMap.sentiment[agg.groupName]
          : undefined,
    n_data_sources: agg.nDataSources,
    row_kind: agg.groupName2 as RowKind | undefined,
  };
}

export function mapToIntelligenceDetailsTableRow(
  agg: InteractionAggregates,
  enableLLMIssuesV2?: boolean,
): IntelligenceDetailsTableRow {
  return {
    ...baseMapToIntelligenceRow(agg, enableLLMIssuesV2),
    sort_percentage: 0,
    retention: agg.retention ?? 0,
    sentiment_score: agg.sentimentScore,
    intent_is_merged: !!agg.isCustom,
  };
}

// =====================================================
// UTILS
// =====================================================
// function calcSortPercentage(
//   item: InteractionAggregates,
//   sortBy: QueryOrdering,
//   maxValues: InteractionAggregates | undefined,
//   isLoadingAggregates: boolean,
// ) {
//   if (!item || !maxValues || isLoadingAggregates) return;

//   if (sortBy.by === 'n_interactions') {
//     return (item.nInteractions ?? 0) / (maxValues?.nInteractions ?? 1);
//   } else if (sortBy.by === 'n_users') {
//     return (item.nUsers ?? 0) / (maxValues?.nUsers ?? 1);
//   } else if (sortBy.by === 'n_sessions') {
//     return (item.nSessions ?? 0) / (maxValues?.nSessions ?? 1);
//   } else if (sortBy.by === 'retention') {
//     return (item.retention ?? 0) / (maxValues?.retention ?? 1);
//   } else if (sortBy.by === 'session_length') {
//     return (item.sessionLength ?? 0) / (maxValues?.sessionLength ?? 1);
//   } else if (sortBy.by === 'session_time') {
//     return (item.sessionTime ?? 0) / (maxValues?.sessionTime ?? 1);
//   } else if (sortBy.by === 'negative_feedback') {
//     return (
//       ((item.nNegativeExplicitUserFeedback ?? 0) + (item.nNegativeImplicitUserFeedback ?? 0)) /
//       ((maxValues?.nNegativeExplicitUserFeedback ?? 0) + (maxValues?.nNegativeImplicitUserFeedback ?? 0))
//     );
//   } else if (sortBy.by === 'positive_feedback') {
//     return (
//       ((item.nPositiveExplicitUserFeedback ?? 0) + (item.nPositiveImplicitUserFeedback ?? 0)) /
//       ((maxValues?.nPositiveExplicitUserFeedback ?? 0) + (maxValues?.nPositiveImplicitUserFeedback ?? 0))
//     );
//   } else if (sortBy.by === 'conversation_percentage_negative_feedback') {
//     return (item.conversationErrorRate ?? 0) / (maxValues?.conversationErrorRate ?? 1);
//   } else if (sortBy.by === 'percentage_negative_feedback') {
//     return (item.percentageNegativeFeedback ?? 0) / (maxValues?.percentageNegativeFeedback ?? 1);
//   } else if (sortBy.by === 'negative_explicit_feedback') {
//     return (item.nNegativeExplicitUserFeedback ?? 0) / (maxValues?.nNegativeExplicitUserFeedback ?? 1);
//   } else if (sortBy.by === 'positive_explicit_feedback') {
//     return (item.nPositiveExplicitUserFeedback ?? 0) / (maxValues?.nPositiveExplicitUserFeedback ?? 1);
//   }

//   return;
// }
